import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Card, CardContent, Grid, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  CustomTextField,
  LoadingFakeText,
  DateTime,
} from "@totalsoft/rocket-webapp-components-deprecated";
import { get, set } from "@totalsoft/change-tracking-react";
import { onTextBoxChange } from "utils/propertyChangeAdapters";
import { emptyString, emptyArray } from "constants/defaultValues";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { CITIES_QUERY, GENDER_VALUES_QUERY, LEGAL_FORM_VALUES_QUERY, USER_DATA } from "features/common/queries";
import { legalForm, gender } from "constants/queryTypes";
import { useApolloClient } from "@apollo/client";
import { getErrors, isValid } from "@totalsoft/pure-validations";
import { ArrowBack } from "@material-ui/icons";
import { cityTypes } from "features/common/constants";

function CompanyOrIndividualData({ selectedClientLens, isCompany, validation, onBack }) {
  const { t } = useTranslation();
  const client = useApolloClient();

  const [legalFormValues, setLegalFormValues] = useState(emptyArray);
  const [genderValues, setGenderValues] = useState(emptyArray);
  const [countries, setCountries] = useState(emptyArray);

  const { loading: loadingLegalFormValues } = useQueryWithErrorHandling(LEGAL_FORM_VALUES_QUERY, {
    variables: {
      type: legalForm,
    },
    onCompleted: (data) => {
      setLegalFormValues(data.legalFormValues);
    },
  });

  const { loading: loadingGenderValues } = useQueryWithErrorHandling(GENDER_VALUES_QUERY, {
    variables: {
      type: gender,
    },
    onCompleted: (data) => {
      setGenderValues(data.genderValues);
    },
  });

  const { loading: loadingCountries } = useQueryWithErrorHandling(CITIES_QUERY, {
    variables: {
      cityTypeId: cityTypes.country,
    },
    onCompleted: (data) => {
      setCountries(data.cities);
      client.watchQuery({ query: USER_DATA, fetchPolicy: "cache-only" }).subscribe({
        next: ({ data }) => {
          (selectedClientLens.countryId |> set)(data?.userData?.countryId);
        },
      });
    },
  });

  if (loadingLegalFormValues || loadingCountries || loadingGenderValues) {
    return <LoadingFakeText lines={10} />;
  }

  return (
    <Card>
      <CardContent>
        {isCompany ? (
          <>
            <Typography variant="h4">{t("Client.CompanyData")}</Typography>
            <Grid container>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.CompanyName")}
                    value={(selectedClientLens.companyName |> get) || emptyString}
                    onChange={selectedClientLens.companyName |> set |> onTextBoxChange}
                    required
                    error={!isValid(validation.companyName)}
                    helperText={getErrors(validation.companyName)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    value={(selectedClientLens.legalFormId |> get) || emptyString}
                    label={t("Client.LegalForm") + " *"}
                    simpleValue={true}
                    options={legalFormValues}
                    valueKey="id"
                    labelKey="name"
                    onChange={selectedClientLens.legalFormId |> set}
                    isClearable={true}
                    error={!isValid(validation.legalFormId)}
                    helperText={getErrors(validation.legalFormId)}
                    noOptionsText={t("General.AutocompleteNoOptions")}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.TaxIdNumber")}
                    value={(selectedClientLens.taxId |> get) || emptyString}
                    onChange={selectedClientLens.taxId |> set |> onTextBoxChange}
                    required
                    error={!isValid(validation.taxId)}
                    helperText={getErrors(validation.taxId)}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.Street")}
                    value={(selectedClientLens.street1 |> get) || emptyString}
                    onChange={selectedClientLens.street1 |> set |> onTextBoxChange}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.Number")}
                    value={(selectedClientLens.number |> get) || emptyString}
                    onChange={selectedClientLens.number |> set |> onTextBoxChange}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.City")}
                    value={(selectedClientLens.city |> get) || emptyString}
                    onChange={selectedClientLens.city |> set |> onTextBoxChange}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    value={(selectedClientLens.countryId |> get) || emptyString}
                    label={t("Client.Country") + " *"}
                    simpleValue={true}
                    options={countries}
                    valueKey="id"
                    labelKey="name"
                    onChange={selectedClientLens.countryId |> set}
                    isClearable={true}
                    error={!isValid(validation.countryId)}
                    helperText={getErrors(validation.countryId)}
                    disabled={Boolean(selectedClientLens.countryId |> get)}
                    noOptionsText={t("General.AutocompleteNoOptions")}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.Zip")}
                    value={(selectedClientLens.zip |> get) || emptyString}
                    onChange={selectedClientLens.zip |> set |> onTextBoxChange}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Typography variant="h4">{t("Client.IndividualData")}</Typography>
            <Grid container>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.FirstName")}
                    value={(selectedClientLens.firstName |> get) || emptyString}
                    onChange={selectedClientLens.firstName |> set |> onTextBoxChange}
                    required
                    error={!isValid(validation.firstName)}
                    helperText={getErrors(validation.firstName)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.LastName")}
                    value={(selectedClientLens.lastName |> get) || emptyString}
                    onChange={selectedClientLens.lastName |> set |> onTextBoxChange}
                    required
                    error={!isValid(validation.lastName)}
                    helperText={getErrors(validation.lastName)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    value={(selectedClientLens.genderId |> get) || emptyString}
                    label={t("Client.Gender") + " *"}
                    simpleValue={true}
                    options={genderValues}
                    valueKey="id"
                    labelKey="name"
                    onChange={selectedClientLens.genderId |> set}
                    isClearable={true}
                    error={!isValid(validation.genderId)}
                    helperText={getErrors(validation.genderId)}
                    noOptionsText={t("General.AutocompleteNoOptions")}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <DateTime
                    fullWidth
                    disableToolbar={false}
                    label={t("Client.BirthDate")}
                    value={(selectedClientLens.birthDate |> get) || emptyString}
                    onChange={selectedClientLens.birthDate |> set}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.IdentifyCardNo")}
                    value={(selectedClientLens.identityCardNo |> get) || emptyString}
                    onChange={selectedClientLens.identityCardNo |> set |> onTextBoxChange}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.PIN")}
                    value={(selectedClientLens.pin |> get) || emptyString}
                    onChange={selectedClientLens.pin |> set |> onTextBoxChange}
                    required
                    error={!isValid(validation.pin)}
                    helperText={getErrors(validation.pin)}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.Phone")}
                    value={(selectedClientLens.phone |> get) || emptyString}
                    onChange={selectedClientLens.phone |> set |> onTextBoxChange}
                    required
                    error={!isValid(validation.phone)}
                    helperText={getErrors(validation.phone)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.Street")}
                    value={(selectedClientLens.street1 |> get) || emptyString}
                    onChange={selectedClientLens.street1 |> set |> onTextBoxChange}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.Number")}
                    value={(selectedClientLens.number |> get) || emptyString}
                    onChange={selectedClientLens.number |> set |> onTextBoxChange}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.City")}
                    value={(selectedClientLens.city |> get) || emptyString}
                    onChange={selectedClientLens.city |> set |> onTextBoxChange}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    value={(selectedClientLens.countryId |> get) || emptyString}
                    label={t("Client.Country") + " *"}
                    simpleValue={true}
                    options={countries}
                    valueKey="id"
                    labelKey="name"
                    onChange={selectedClientLens.countryId |> set}
                    isClearable={true}
                    error={!isValid(validation.countryId)}
                    helperText={getErrors(validation.countryId)}
                    disabled={Boolean(selectedClientLens.countryId |> get)}
                    noOptionsText={t("General.AutocompleteNoOptions")}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    fullWidth
                    label={t("Client.Zip")}
                    value={(selectedClientLens.zip |> get) || emptyString}
                    onChange={selectedClientLens.zip |> set |> onTextBoxChange}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
        <Grid item container justifyContent="flex-end">
          <Button
            id="createClient_BackToSearchBtn"
            right="true"
            variant="contained"
            color="primary"
            onClick={onBack}
            endIcon={<ArrowBack />}
          >
            {t("General.BackToSearch")}
          </Button>
        </Grid>
      </CardContent>
    </Card>
  );
}

CompanyOrIndividualData.propTypes = {
  selectedClientLens: PropTypes.object.isRequired,
  isCompany: PropTypes.bool.isRequired,
  validation: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default CompanyOrIndividualData;
